import type { FormEvent, ChangeEvent, ReactElement } from "react";
import { useRef } from "react";
import { Size } from "../UiEnums";
import "./SearchBar.scss";

interface Properties {
  width?: string;
  size?: Size;
  placeholder?: string;
  input: string;
  onInputChanged: (input: string) => void;
  onSearch: (input: string) => void;
  onFocus?: () => void;
  searchOnKeyStroke?: boolean;
  maxLength?: number;
  disabled?: boolean;
}

export default function ControlledSearchBar(props: Properties): ReactElement {
  const textInput = useRef<HTMLInputElement>(null);

  function handleSearch(evt: FormEvent<HTMLFormElement>): void {
    evt.preventDefault();
    const formData = new FormData(evt.target as HTMLFormElement);
    const formJson = Object.fromEntries(formData.entries());
    const text = formJson.searchText as string;
    props.onSearch(text);
  }

  function handleInputChange(evt: ChangeEvent<HTMLInputElement>): void {
    if (props.searchOnKeyStroke ?? false) {
      props.onSearch(evt.target.value);
    } else {
      props.onInputChanged(evt.target.value);
    }
  }

  function clearInput(): void {
    props.onInputChanged("");
  }

  function limitSize(): Size {
    const size = props.size ?? Size.MEDIUM;
    // Cap generic button size to what's defined in design system:
    const capSize = (): Size => {
      switch (props.size) {
        case Size.XXXLARGE:
        case Size.XXLARGE:
        case Size.XLARGE:
          return Size.LARGE;
        case Size.XSMALL:
          return Size.SMALL;
        default:
          return size;
      }
    };
    const cappedSize = capSize();
    if (cappedSize !== size) {
      console.warn(
        `ControlledSearchBar isn't available for size ${size}, using ${cappedSize} instead`,
      );
    }
    return cappedSize;
  }

  function sizeClass(): string {
    return Size[limitSize()].toLocaleLowerCase();
  }

  return (
    <div
      className={`search-bar ${sizeClass()} ${props.disabled === true && "disabled"}`}
      style={{ width: props.width }}
    >
      <form onSubmit={handleSearch}>
        <input
          type="text"
          ref={textInput}
          className="search-input"
          name="searchText"
          placeholder={props.placeholder}
          value={props.input}
          onChange={handleInputChange}
          onFocus={props.onFocus}
          autoComplete="off"
          maxLength={props.maxLength}
          disabled={props.disabled}
        />
        <button
          type="button"
          tabIndex={-1}
          className={props.input.length > 0 ? "clear-icon" : "search-icon"}
          onClick={clearInput}
          disabled={props.disabled}
        />
      </form>
    </div>
  );
}
